/**
 * WkdUploadCore - 文件上传核心组件
 * 
 * 基于 antd Upload 封装，支持图片/视频上传、尺寸校验、比例校验等
 */
import React, { useRef, ReactNode } from 'react';
import { Upload, UploadProps } from 'antd';
import { DEFAULT_UPLOAD_TYPE, MEDIA_LIMIT_TYPE, UploadType } from './constants';
import { checkImageAttr, checkVideoAttr, getVideoInfo, getImageInfo, FileInfo } from './util';

const { Dragger } = Upload;

declare global {
  interface Window {
    __SYSTEM_VERSION__?: string;
  }
}

export interface UploadFileInfo {
  uid: string;
  size?: number;
  name: string;
  md5?: string;
  url: string;
  cover?: string;
  width?: number;
  height?: number;
  duration?: number;
  isLastFile: boolean;
}

export interface WkdUploadCoreProps extends Omit<UploadProps, 'onChange'> {
  /** 上传类型：default | dragger */
  type?: UploadType;
  /** 文件大小限制 (KB)，可以是数字或 [min, max] 范围 */
  size?: number | [number, number];
  /** 宽高比限制，可以是数字或 [min, max] 范围 */
  ratio?: number | [number, number];
  /** 上传目录 */
  dir?: string;
  /** 是否禁用 webp */
  disableWebp?: boolean;
  /** 视频时长限制 (秒)，可以是数字或 [min, max] 范围 */
  duration?: number | [number, number];
  /** 上传接口地址 */
  action?: string;
  /** 额外请求参数 */
  reqParam?: Record<string, any>;
  /** 上传前回调 */
  onBeforeUpload?: (fileInfo: FileInfo) => Promise<any>;
  /** 上传成功回调 */
  onChange?: (fileInfo: UploadFileInfo, webpInfo?: UploadFileInfo, response?: any) => void;
  /** 上传失败回调 */
  onFaild?: (error: string, file?: any) => void;
  /** 上传进度回调 */
  onProgress?: (percent: number, file: any) => void;
  /** 包装器类名 */
  wrapClassName?: string;
  /** 是否禁用校验 */
  disableCheck?: boolean;
  children?: ReactNode;
}

const WkdUploadCore: React.FC<WkdUploadCoreProps> = ({ children, ...props }) => {
  const {
    type = DEFAULT_UPLOAD_TYPE,
    size,
    ratio,
    dir = 'common/assets',
    accept,
    disabled,
    duration,
    action = '/api/cdnUploadV3',
    reqParam = {},
    onBeforeUpload = () => Promise.resolve(),
    onChange = () => {},
    onFaild = () => {},
    onProgress = () => {},
    headers = {},
    wrapClassName = '',
    disableCheck = false,
    ...otherProps
  } = props;

  const fileNumRef = useRef(0);

  const options: UploadProps = {
    disabled,
    accept,
    name: 'file',
    data: {
      dir,
      ...reqParam,
    },
    headers: {
      'x-system-version': window.__SYSTEM_VERSION__ || '',
      ...headers,
    },
    action,
    showUploadList: false,
    beforeUpload(file) {
      let fn: (opts: any) => Promise<FileInfo>;
      fileNumRef.current++;

      if (file.type.indexOf('image/') !== -1 && file.type.indexOf('vnd.adobe.photoshop') === -1) {
        fn = disableCheck ? getImageInfo : checkImageAttr;
      } else if (file.type.indexOf('video/') !== -1) {
        fn = disableCheck ? getVideoInfo : checkVideoAttr;
      } else {
        fn = () => Promise.resolve({ file, size: file.size });
      }

      return fn({
        file,
        size,
        ratio,
        duration,
      })
        .then(onBeforeUpload)
        .catch((err) => {
          fileNumRef.current--;
          if (typeof err === 'string') {
            console.log('Upload error:', err);
            onFaild(err);
          }
          return Promise.reject();
        });
    },
    onChange(info) {
      if (info.file.status === 'uploading') {
        onProgress(parseInt(String(info?.event?.percent || 0), 10), info.file);
      } else if (info.file.status === 'done' && info.file.response?.code === 0) {
        fileNumRef.current--;
        const response = info.file.response;
        const originInfo = response?.data?.originInfo;
        
        onChange(
          {
            uid: info.file.uid,
            size: originInfo?.size,
            name: info.file.name,
            md5: originInfo?.md5,
            url: response?.data?.location,
            cover: originInfo?.cover,
            width: originInfo?.width,
            height: originInfo?.height,
            duration: originInfo?.duration,
            isLastFile: fileNumRef.current === 0,
          },
          response?.data?.locationWebp
            ? {
                uid: info.file.uid,
                size: response?.data?.webpInfo?.size,
                name: info.file.name,
                md5: response?.data?.webpInfo?.md5,
                url: response?.data?.locationWebp,
                width: response?.data?.webpInfo?.width,
                height: response?.data?.webpInfo?.height,
                isLastFile: fileNumRef.current === 0,
              }
            : undefined,
          response,
        );
      } else if (
        info.file.status === 'error' ||
        (info.file.status === 'done' && info.file.response?.code !== 0)
      ) {
        fileNumRef.current--;
        onFaild(MEDIA_LIMIT_TYPE.UPLOAD_ERROR, info.file);
      }
    },
  };

  return type === DEFAULT_UPLOAD_TYPE ? (
    <Upload className={wrapClassName || ''} {...options} {...otherProps}>
      {children}
    </Upload>
  ) : (
    <Dragger className={`${wrapClassName || ''} music-drag-upload`} {...options} {...otherProps}>
      {children}
    </Dragger>
  );
};

export default WkdUploadCore;

// 导出工具函数和常量
export { MEDIA_LIMIT_TYPE, DEFAULT_UPLOAD_TYPE, DRAGGER_UPLOAD_TYPE } from './constants';
export type { MediaLimitType, UploadType } from './constants';
export { checkImageAttr, checkVideoAttr, getImageInfo, getVideoInfo } from './util';
export type { FileInfo, FileCheckOptions } from './util';
